const CROSSFADE_DURATION_MS = 1000;
// Safari's linearRampToValueAtTime doesn't work from 0, so use a tiny value
const GAIN_MIN = 0.0001;

export type CreateAudioError = "playback_failed" | "load_failed";

type CreateAudioInput = {
  src: string;
  loop?: boolean;
  /** Whether to fade volume in/out on play/pause. Defaults to `true`. */
  fade?: boolean;
  onLoaded?: ({ analyser }: { analyser: AnalyserNode | null }) => void;
  onPlayStarted?: () => void;
  onEnded?: () => void;
  onError?: (error: CreateAudioError) => void;
  onMetadata?: (metadata: { duration: number }) => void;
};

export type CreateAudioReturn = {
  load: (options?: { time?: number }) => void;
  play: (options?: { time?: number }) => Promise<void>;
  pause: () => void;
  cleanup: () => void;
  getAudio: () => HTMLAudioElement | null;
  isLoaded: () => boolean;
  getSrc: () => string;
};

export function createAudio(input: CreateAudioInput): CreateAudioReturn {
  let controller: AbortController | null = null;
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let audio: HTMLAudioElement | null = null;
  let gainNode: GainNode | null = null;
  let fadeTimeout: number | null = null;
  let loaded = false;

  const load = ({ time }: { time?: number } = {}) => {
    if (audioContext) return;

    controller = new AbortController();
    audioContext = new AudioContext();
    analyser = audioContext.createAnalyser();
    audio = new Audio();
    audio.crossOrigin = "anonymous";
    audio.loop = input.loop ?? false;

    // Add listeners before setting src (in case audio is cached and fires synchronously)
    audio.addEventListener(
      "canplaythrough",
      () => {
        loaded = true;
        input.onLoaded?.({ analyser });
      },
      { signal: controller.signal, once: true },
    );

    audio.addEventListener(
      "loadedmetadata",
      () => {
        if (audio && typeof time === "number") {
          audio.currentTime = time;
        }
        input.onMetadata?.({ duration: audio?.duration ?? 0 });
      },
      { signal: controller.signal, once: true },
    );

    audio.addEventListener(
      "ended",
      () => {
        input.onEnded?.();
      },
      { signal: controller.signal },
    );

    audio.addEventListener(
      "error",
      () => {
        cleanup();
        input.onError?.("load_failed");
      },
      { signal: controller.signal },
    );

    audio.src = input.src;

    gainNode = audioContext.createGain();
    gainNode.gain.value = GAIN_MIN;

    const sourceNode = audioContext.createMediaElementSource(audio);
    sourceNode.connect(gainNode);
    gainNode.connect(audioContext.destination);
    sourceNode.connect(analyser);
  };

  const cancelFade = () => {
    if (fadeTimeout) {
      window.clearTimeout(fadeTimeout);
      fadeTimeout = null;
    }
    if (gainNode) {
      // Safari fix: read value, cancel, then set it back
      const currentValue = gainNode.gain.value;
      gainNode.gain.cancelScheduledValues(0);
      gainNode.gain.value = Math.max(currentValue, GAIN_MIN);
    }
  };

  const play = async (options: { time?: number } = {}) => {
    if (!audioContext || !audio || !gainNode) return;
    cancelFade();

    if (audio.paused && typeof options.time === "number") {
      if (options.time > 0 && options.time < audio.duration) {
        audio.currentTime = options.time;
      } else {
        audio.currentTime = 0;
      }
    }

    if (audioContext.state === "suspended") {
      await Promise.race([audioContext.resume(), new Promise<void>(resolve => window.setTimeout(resolve, 100))]);
    }

    // If AudioContext is still suspended after trying to resume, audio won't be audible
    if (audioContext.state === "suspended") {
      input.onError?.("playback_failed");
      return;
    }

    if (input.fade === false) {
      gainNode.gain.value = 1;
    } else {
      const now = audioContext.currentTime;
      // Safari fix: ensure we start from a non-zero value and set it explicitly
      const startValue = Math.max(gainNode.gain.value, GAIN_MIN);
      gainNode.gain.value = startValue;
      gainNode.gain.setValueAtTime(startValue, now);
      gainNode.gain.linearRampToValueAtTime(1, now + CROSSFADE_DURATION_MS / 1000);
    }

    if (audio.paused) {
      try {
        await audio.play();
        input.onPlayStarted?.();
      } catch {
        input.onError?.("playback_failed");
      }
    } else {
      input.onPlayStarted?.();
    }
  };

  const pause = () => {
    if (!audioContext || !audio || !gainNode) return;
    cancelFade();

    if (input.fade === false) {
      audio.pause();
    } else {
      const now = audioContext.currentTime;
      // Safari fix: set value explicitly before ramping
      gainNode.gain.setValueAtTime(gainNode.gain.value, now);
      gainNode.gain.linearRampToValueAtTime(GAIN_MIN, now + CROSSFADE_DURATION_MS / 1000);

      fadeTimeout = window.setTimeout(() => {
        fadeTimeout = null;
        audio?.pause();
      }, CROSSFADE_DURATION_MS);
    }
  };

  const cleanup = () => {
    cancelFade();
    controller?.abort();
    controller = null;
    loaded = false;
    if (audio) {
      audio.pause();
      audio.src = "";
      audio = null;
    }
    if (audioContext) {
      audioContext.close();
      audioContext = null;
    }
  };

  const getAudio = () => audio;
  const isLoaded = () => loaded;
  const getSrc = () => input.src;

  return {
    load,
    play,
    pause,
    cleanup,
    getAudio,
    isLoaded,
    getSrc,
  };
}
